import { useLighterAccountsQuery } from '@funkit/connect-core'
import { getLighterWithdrawalDelay } from '@funkit/connect-core/clients/lighter'
import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'

import { logger } from '../../src/logging/logger'
import { useFunkitTranslation } from '../../src/providers/FunkitI18nProvider'
import {
  LIGHTER_FAST_METHOD_ID,
  LIGHTER_SECURE_METHOD_ID,
  type LighterWithdrawalFlowConfig,
} from './lighterWithdrawalConfig'

/** One row on the withdrawal method screen, before icons are attached. */
export interface LighterWithdrawalMethod {
  id: typeof LIGHTER_FAST_METHOD_ID | typeof LIGHTER_SECURE_METHOD_ID
  title: string
  subtitle: string
}

export interface UseLighterWithdrawalMethodsResult {
  /** Fast then Secure, in web's order. Empty until the account resolves. */
  methods: LighterWithdrawalMethod[]
  /**
   * The user's Lighter L2 main-account index. Both rails address the account
   * by index, so neither can run before this resolves.
   */
  accountIndex: string | undefined
  /** Unified accounts hold one USDC balance — no spot/perps split. */
  isUnifiedTradingMode: boolean
  /** The account lookup is in flight; rows are expected to be empty. */
  isLoading: boolean
}

const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR

/**
 * Formats Lighter's live L2→L1 delay for the Secure row (e.g. "1 h 50 min").
 * - Reproduces web's `formatSecondsToReadableForm(seconds, false, true)` output
 *   for every input, including its two oddities: a sub-minute delay reads in
 *   seconds, and exactly one hour reads "60 min". The two SDKs quote the same
 *   bridge, so they must not disagree on the wait.
 * - Rounds up otherwise: an estimate must not promise finality early.
 * - One literal `t` key per shape — never a computed key.
 */
function formatDelay(t: TFunction, seconds: number): string {
  const normalized = Math.ceil(seconds)
  if (normalized === 1) {
    return t('withdrawLighter.delaySecond')
  }
  if (normalized <= SECONDS_PER_MINUTE) {
    return t('withdrawLighter.delaySeconds', { seconds: normalized })
  }
  // Round to minutes first: rounding the remainder alone can yield "1 h 60 min".
  const minutes = Math.ceil(normalized / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR
  const hours = Math.ceil(
    (normalized - minutes * SECONDS_PER_MINUTE) / SECONDS_PER_HOUR,
  )
  if (hours === 1 && minutes === 0) {
    return t('withdrawLighter.delayMinutes', { minutes: MINUTES_PER_HOUR })
  }
  if (hours > 0 && minutes > 0) {
    return t('withdrawLighter.delayHoursMinutes', { hours, minutes })
  }
  if (hours > 0) {
    return t('withdrawLighter.delayHours', { hours })
  }
  return t('withdrawLighter.delayMinutes', { minutes })
}

/**
 * Secure-withdrawal delay as display copy. Shown live because it tracks L2→L1
 * batch cadence; `undefined` until it loads.
 */
function useLighterSecureDelayText(enabled: boolean): string | undefined {
  const { t } = useFunkitTranslation()
  const { data } = useQuery({
    queryKey: ['lighterWithdrawalDelay'],
    queryFn: getLighterWithdrawalDelay,
    // This hook runs for every withdrawal flow, Lighter or not — a host with no
    // Lighter config must never reach Lighter's API.
    enabled,
    staleTime: 60_000,
    retry: 2,
  })
  const seconds = data?.seconds
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return undefined
  }
  return formatDelay(t, seconds)
}

type RailOutcome = 'not_configured' | 'loading' | 'offered' | 'no_account_index'

/**
 * Whether Lighter's rails replace the generic crypto row, and why not when they
 * don't.
 * - `isFetched`, not `isLoading`: the latter is false on the first render,
 *   before the query starts, which would record a premature "no account".
 */
function resolveRailOutcome({
  isConfigured,
  isFetched,
  hasAccountIndex,
}: {
  isConfigured: boolean
  isFetched: boolean
  hasAccountIndex: boolean
}): RailOutcome {
  if (!isConfigured) {
    return 'not_configured'
  }
  if (!isFetched) {
    return 'loading'
  }
  if (!hasAccountIndex) {
    return 'no_account_index'
  }
  return 'offered'
}

/**
 * The Fast / Secure rows plus the account state both rails need. Port of the
 * row-building half of web's `useLighterWithdrawalConfig`.
 *
 * Pass `undefined` when the host configured no Lighter: the inner hooks still
 * run (Rules of Hooks) but every query stays disabled.
 */
export function useLighterWithdrawalMethods(
  config: LighterWithdrawalFlowConfig | undefined,
): UseLighterWithdrawalMethodsResult {
  const { t } = useFunkitTranslation()
  const { mainAccountIndex, isUnifiedTradingMode, isLoading, isFetched } =
    useLighterAccountsQuery({ address: config?.l1Address })
  const secureDelayText = useLighterSecureDelayText(!!config)

  // Lighter addresses accounts by a numeric index; anything else means the
  // lookup returned something unusable and neither rail can address the user.
  const accountIndex =
    mainAccountIndex && /^\d+$/.test(mainAccountIndex)
      ? mainAccountIndex
      : undefined

  const outcome = resolveRailOutcome({
    isConfigured: !!config,
    isFetched,
    hasAccountIndex: !!accountIndex,
  })
  // Rail-availability decision point, recorded once per outcome so a re-render
  // or a background refetch doesn't re-emit the same one.
  const loggedOutcome = useRef<RailOutcome | undefined>(undefined)
  useEffect(() => {
    if (outcome === 'loading' || loggedOutcome.current === outcome) {
      return
    }
    loggedOutcome.current = outcome
    if (outcome === 'not_configured') {
      return
    }
    const payload = {
      outcome,
      isUnifiedTradingMode,
      hasAccountIndex: !!accountIndex,
    }
    if (outcome === 'no_account_index') {
      // Recovered, but the user loses Lighter's rails for this session — that
      // is a visible capability change, so it belongs above INFO.
      logger.warn('lighter:withdrawal:rails', payload)
      return
    }
    logger.info('lighter:withdrawal:rails', payload)
  }, [outcome, isUnifiedTradingMode, accountIndex])

  const methods: LighterWithdrawalMethod[] =
    config && accountIndex
      ? [
          {
            id: LIGHTER_FAST_METHOD_ID,
            title: t('withdrawLighter.methodFast'),
            subtitle: isUnifiedTradingMode
              ? t('withdrawLighter.fastDisclaimerUnified')
              : t('withdrawLighter.fastDisclaimer'),
          },
          {
            id: LIGHTER_SECURE_METHOD_ID,
            title: t('withdrawLighter.methodSecure'),
            subtitle: t('withdrawLighter.secureDisclaimer', {
              // Until the live value loads, show the localized "60+ minutes"
              // historical estimate. The "+" lives in the per-locale fallback
              // string so it doesn't bleed into live values, which are exact.
              delay:
                secureDelayText ?? t('withdrawLighter.secureFallbackDelay'),
            }),
          },
        ]
      : []

  return {
    methods,
    accountIndex,
    isUnifiedTradingMode,
    isLoading: !!config && isLoading,
  }
}
